<script setup lang="ts">
/**
 * The table as it will be printed, beside the cells that make it.
 *
 * Drawn by the page's own table block, on a strip of the report's own paper,
 * at the width the page gives it and with the document's type, rules and
 * colours. Not an approximation of the page: the same component with the same
 * stylesheet, so a merge that looks right here looks right in the PDF.
 *
 * Shrunk to fit the pane, never grown past its real size. The measurements
 * stay real; only the viewing size changes, which is why the scale is on a
 * wrapper and never on the paper's own type.
 *
 * It also shows what is selected in the grid, and a click on a cell here
 * selects it there: somebody looking at the printed table and thinking "that
 * heading" can point at it where they are looking.
 */
import { useI18n } from 'vue-i18n'

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import { parseMeasure } from '@/domain/layout'
import type { TableSection } from '@/domain/section'
import { PX_PER_MM } from '@/domain/sheets'
import TableBlock from '@/sections/table/TableBlock.vue'
import type { CellPoint, CellTarget } from '@/sections/table/edit'
import { useReportStore } from '@/stores/report'

const { t } = useI18n()

defineProps<{
  section: TableSection
  target: CellTarget | null
}>()

const emit = defineEmits<{ pick: [point: CellPoint, extend: boolean] }>()

const report = useReportStore()

/** The pane the paper sits in, and the paper itself, both measured. */
const pane = ref<HTMLElement | null>(null)
const paper = ref<HTMLElement | null>(null)
const paneWidth = ref(0)
const paperHeight = ref(0)

/** The page's width in pixels, at the 96 to the inch the page is drawn at. */
const pageWidth = computed(() => (parseMeasure(report.layout.pageWidth) ?? 210) * PX_PER_MM)

/** Room kept either side of the paper, so its edge and shadow can be seen. */
const GUTTER = 48

const scale = computed(() =>
  paneWidth.value === 0
    ? 1
    : Math.min(1, Math.max(0.3, (paneWidth.value - GUTTER) / pageWidth.value)),
)

/**
 * The room the shrunk paper takes.
 *
 * A transform does not change the space an element holds, so without this the
 * pane would scroll through the paper's full, unshrunk height.
 */
const sizer = computed(() => ({
  width: `${pageWidth.value * scale.value}px`,
  height: `${paperHeight.value * scale.value}px`,
}))

let observer: ResizeObserver | null = null

onMounted(() => {
  observer = new ResizeObserver(() => {
    paneWidth.value = pane.value?.clientWidth ?? 0
    paperHeight.value = paper.value?.offsetHeight ?? 0
  })
  if (pane.value) observer.observe(pane.value)
  if (paper.value) observer.observe(paper.value)
})

onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
})
</script>

<template>
  <section class="preview" :aria-label="t('inspector.table.preview')">
    <header class="preview__head">
      <span class="b-label">{{ t('inspector.table.preview') }}</span>
      <span class="preview__hint">{{ t('inspector.table.previewHint') }}</span>
    </header>

    <div ref="pane" class="preview__desk">
      <div class="preview__sizer" :style="sizer">
        <div class="preview__stage" :style="{ transform: `scale(${scale})` }">
          <article ref="paper" class="page preview__paper" :style="report.pageStyle">
            <TableBlock
              :section="section"
              :picked="target"
              pickable
              @pick="(point, extend) => emit('pick', point, extend)"
            />
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.preview {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  border-top: 1px solid var(--hairline);
  background: var(--desk, var(--panel-raised));
}

.preview__head {
  display: flex;
  align-items: baseline;
  gap: var(--sp-4);
  padding: var(--sp-5) var(--sp-6) 0;
}

.preview__hint {
  color: var(--ink-faint);
  font-size: var(--text-xs);
}

.preview__desk {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: var(--sp-5) 0 var(--sp-7);
}

.preview__sizer {
  position: relative;
  margin: 0 auto;
}

.preview__stage {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
}

/* A strip of the page rather than a whole sheet: the table and some paper
   around it. The side margins are the page's own, so the table is exactly as
   wide as it will be printed. */
.preview__paper {
  width: var(--page-width);
  min-height: 0;
  margin: 0;
  padding: 12mm var(--page-margin-side);
}
</style>
